// ============================================================================
// LE TABLEAU DES STATISTIQUES — le squelette que partagent les reportings (ventes, articles) : niveaux collés à gauche,
// en-tête et ligne de total collés en haut, tri par en-tête, colonnes choisies et mémorisées par compte.
//
// ⚠️ IL NE DÉFINIT AUCUN NOMBRE ET N'EN MET AUCUN EN FORME : chaque onglet lui donne ses cellules déjà écrites. Il
//    porte ce que les deux tableaux doivent faire PAREIL — écrit deux fois, l'un aurait trié un « — » en tête et l'autre
//    en queue, et l'en-tête d'un onglet ne se serait plus aligné sur ses lignes.
// ============================================================================
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ColumnsButton, type ColumnsButtonColumn } from '@orgazone/ui-web';
import { reconcileColumnLayout, type ColumnLayout } from '@orgazone/ui-tokens';
import { api } from '../api';
import { t } from '../i18n';
import { shownValue } from './stats-format';

/** Le tri : une colonne (`ax:<niveau>` pour un niveau), son sens, et s'il a été choisi (sinon, l'ordre des niveaux). */
export interface StatsSort {
  k: string;
  d: 1 | -1;
  man: boolean;
}

/** La largeur d'un niveau ; le dernier s'élargit pour prendre la place restante. */
export const AXIS_WIDTH = 158;

/**
 * LA LARGEUR D'UNE COLONNE D'INDICATEUR — jamais moins que son libellé d'en-tête sur UNE ligne (capitales 10,5 px
 * grasses, interlettrage .05em), plus ses marges et la flèche de tri.
 * ⚠️ FIXE, EN PIXELS : l'en-tête, la ligne de total et chaque ligne sont des grilles distinctes, qui ne s'alignent que
 *    si elles portent le même gabarit.
 * ⚠️ `room` : ce qu'on réserve autour du libellé — ses marges (20) et la flèche de tri (16). Un tableau que le dessin pose
 *    à largeurs FIXES (le Reporting taux de sortie) ne réserve rien : son libellé peut mordre sur la marge, comme dans le
 *    dessin, et la colonne ne s'élargit que pour un libellé plus long qu'elle — une traduction.
 */
const widthCache = new Map<string, number>();
export function columnWidth(label: string, min: number, room = 36): number {
  const cached = widthCache.get(`${label}|${min}|${room}`);
  if (cached) return cached;
  let px = label.length * 7.6;
  let sure = false;
  try {
    const g = document.createElement('canvas').getContext('2d');
    if (g) {
      g.font = `700 10.5px ${getComputedStyle(document.body).fontFamily || 'sans-serif'}`;
      px = g.measureText(label.toLocaleUpperCase()).width + label.length * 0.525;
      sure = !document.fonts || document.fonts.status === 'loaded';
    }
  } catch { /* ⚠️ sans canevas (rendu sans navigateur), l'estimation par caractère suffit à ne pas couper */ }
  const w = Math.max(min, Math.ceil(px + room));
  // ⚠️ On ne retient la mesure qu'une fois la police chargée : faite sur la police de repli, elle serait fausse.
  if (sure) widthCache.set(`${label}|${min}|${room}`, w);
  return w;
}

/**
 * LES LIGNES DANS L'ORDRE DEMANDÉ.
 * ⚠️ TANT QU'AUCUN EN-TÊTE N'A ÉTÉ CLIQUÉ, l'ordre est celui des NIVEAUX (chaque niveau par son nom, puis `tie`) : trier
 *    d'emblée sur un chiffre disperserait les regroupements, on ne verrait plus un fournisseur d'un bloc.
 * ⚠️ UNE VALEUR NON MESURÉE PASSE APRÈS TOUTES LES AUTRES, dans les deux sens : un « — » n'est ni petit ni grand.
 */
export function sortStatsRows<R extends { parts: readonly { value: string }[] }>(
  rows: readonly R[], nAxes: number, sort: StatsSort, valueOf: (r: R, k: string) => number | string | null,
  tie: (x: R, y: R) => number, lang: string,
): R[] {
  const collator = new Intl.Collator(lang, { numeric: true, sensitivity: 'base' });
  const byAxes = (x: R, y: R): number => {
    for (let i = 0; i < nAxes; i++) {
      const c = collator.compare(x.parts[i]?.value ?? '', y.parts[i]?.value ?? '');
      if (c) return c;
    }
    return tie(x, y);
  };
  if (!sort.man) return [...rows].sort(byAxes);
  if (sort.k.startsWith('ax:')) {
    const i = Number(sort.k.slice(3));
    return [...rows].sort((x, y) => collator.compare(x.parts[i]?.value ?? '', y.parts[i]?.value ?? '') * sort.d || byAxes(x, y));
  }
  return [...rows].sort((x, y) => {
    const a = valueOf(x, sort.k);
    const b = valueOf(y, sort.k);
    if (a === null || b === null) return a === b ? byAxes(x, y) : a === null ? 1 : -1;
    const c = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
    return c * sort.d || byAxes(x, y);
  });
}

// ─── Les colonnes affichées, mémorisées pour la personne ─────────────────────────────────────────

/**
 * LES COLONNES CHOISIES — visibilité et ordre, mémorisés sur le profil (`bo_table_prefs`, tous les postes).
 * `locked` (une colonne ou plusieurs) ne se masque jamais.
 * ⚠️ BEST-EFFORT ASSUMÉ À LA LECTURE : sans réglage lisible, toutes les colonnes s'affichent dans l'ordre du catalogue ;
 *    un réglage qui n'a pas pu s'ENREGISTRER, lui, se dit (toast), sans quoi on le croirait gardé.
 */
export function useStatsColumns<C extends { k: string }>(
  prefsId: string, catalog: readonly C[], locked: string | readonly string[], labelOf: (c: C) => string, enabled: boolean,
  onSaveFailed: (message: string) => void,
) {
  const keys = catalog.map((c) => c.k);
  const lockedKeys: readonly string[] = typeof locked === 'string' ? [locked] : locked;
  const [layout, setLayout] = useState<ColumnLayout>(() => reconcileColumnLayout(keys, null));
  // ⚠️ LU À L'OUVERTURE DE L'ONGLET, une seule fois : un onglet fermé ne charge rien.
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (!enabled || asked) return;
    setAsked(true);
    let alive = true;
    api.tablePrefs(prefsId)
      .then((p) => { if (alive) setLayout(reconcileColumnLayout(keys, p.layout ?? null)); })
      .catch(() => undefined);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- le catalogue est une constante du module
  }, [enabled]);
  const change = (next: ColumnLayout) => {
    const kept = { ...next, hidden: next.hidden.filter((k) => !lockedKeys.includes(k)) };
    setLayout(kept);
    void api.saveTablePrefs(prefsId, { layout: kept })
      .catch((e: unknown) => onSaveFailed(e instanceof Error ? e.message : String(e)));
  };
  const visible = layout.order
    .map((k) => catalog.find((c) => c.k === k))
    // ⚠️ UNE COLONNE VERROUILLÉE RESTE, même si un réglage ancien ou forgé la dit masquée.
    .filter((c): c is C => !!c && (lockedKeys.includes(c.k) || !layout.hidden.includes(c.k)));
  const pickerColumns: ColumnsButtonColumn[] = catalog.map((c) => ({
    key: c.k, label: labelOf(c), ...(lockedKeys.includes(c.k) ? { locked: true } : {}),
  }));
  return { layout, change, visible, pickerColumns };
}

/** Le bouton « Colonnes » de la barre. */
export function StatsColumnsButton({ columns, layout, onChange }: {
  columns: ColumnsButtonColumn[];
  layout: ColumnLayout;
  onChange: (next: ColumnLayout) => void;
}) {
  return (
    <ColumnsButton columns={columns} layout={layout} onChange={(next) => onChange(next)} reorderable
      persistenceNote={t('Mémorisées sur votre profil, tous vos postes.')} />
  );
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

export const HEAD_TEXT: CSSProperties = {
  fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
};

/** Un niveau de regroupement : son nom, et s'il montre un CODE (chasse fixe, désignation dessous). */
export interface GridAxis { label: string; code?: boolean }
/** Une colonne d'indicateur : son libellé, sa largeur minimale, la couleur de son bloc, et ce que dit son infobulle. */
/**
 * Une colonne d'indicateur ; `fixed` : la largeur du dessin, rien de réservé autour du libellé (`columnWidth`) ; `track` :
 * la piste de grille posée telle quelle (`minmax(92px,1fr)`) ; `sub` : le surtitre de l'en-tête (la période d'une colonne) ;
 * `headBg` / `headFg` : le fond et la couleur de SON en-tête (la colonne d'aujourd'hui du Stock par périodes).
 */
export interface GridColumn {
  k: string; label: string; width: number; color: string; title?: string; fixed?: boolean; track?: string; sub?: string;
  headBg?: string; headFg?: string;
}
/**
 * Une cellule déjà écrite : son texte, sa teinte, sa graisse — et, dans un tableau empilé, la ligne qu'elle porte dessous ;
 * `lines` : une ligne par mesure (le Stock par tailles), chacune avec sa taille, sa graisse et sa teinte.
 */
export interface GridCell {
  text: string; fg: string; fw: number; sub?: { text: string; fg: string };
  lines?: { text: string; fg: string; size: string; fw: number }[];
}
/** Une ligne : la valeur de chaque niveau, ce qu'elle compte (« 3 articles »), et ses cellules. */
export interface GridRow { key: string; parts: readonly { value: string; label: string }[]; sub: string; cells: GridCell[] }

/** Une cellule à plusieurs mesures, une par ligne ; `weight` impose la graisse (la ligne de total). */
function LinesCell({ cell, padding, weight }: { cell: GridCell; padding: string; weight?: number }) {
  return (
    <div role="cell" style={{
      padding, textAlign: 'right', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end',
      justifyContent: 'center',
    }}>
      {(cell.lines ?? []).map((l, i) => (
        <span key={i} style={{ fontSize: l.size, fontWeight: weight ?? l.fw, color: l.fg, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{l.text}</span>
      ))}
    </div>
  );
}

/** Une cellule empilée : sa lecture principale, puis celle qu'elle porte dessous. */
function StackedCell({ cell, padding, size, weight, subSize, subWeight }: {
  cell: GridCell; padding: string; size: string; weight: number; subSize: string; subWeight: number;
}) {
  const line: CSSProperties = { display: 'block', fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 };
  return (
    <div role="cell" style={{ padding, textAlign: 'right', whiteSpace: 'nowrap' }}>
      <span style={{ ...line, fontSize: size, fontWeight: weight, color: cell.fg }}>{cell.text}</span>
      {cell.sub && <span style={{ ...line, fontSize: subSize, fontWeight: subWeight, color: cell.sub.fg }}>{cell.sub.text}</span>}
    </div>
  );
}

/**
 * LE TABLEAU — l'en-tête et la ligne de total collés en haut, les niveaux collés à gauche, puis les lignes ; `after` est
 * ce qui suit (état vide, pied). `busy` dit qu'une lecture est en cours et que ces chiffres sont ceux d'avant.
 * ⚠️ LES NIVEAUX SONT CEUX DE LA RÉPONSE, jamais ceux qu'on est en train de changer : pendant la lecture, l'en-tête
 *    suivrait les nouveaux niveaux et les cellules l'ancienne réponse — les valeurs glisseraient d'une colonne.
 * ⚠️ `stacked` : chaque cellule porte deux lectures d'un même fait (la quantité, puis sa valeur) — les marges et les
 *    tailles du Reporting stock du dessin ; TOUTES les cellules du tableau prennent ce gabarit, même celles d'une seule
 *    ligne, sans quoi les lignes ne s'aligneraient pas.
 * ⚠️ `headColor` et `lastAxis` : ce que le dessin d'UN tableau change au squelette (en-têtes foncés, dernier niveau plus
 *    large au Reporting taux de sortie ; `lastAxis: null` — tous les niveaux à la même largeur, au Stock par périodes ;
 *    `totalSubSize` — la taille de la valeur sous la quantité, sur la ligne de total d'un tableau empilé) — les autres gardent
 *    les valeurs par défaut.
 */
export function StatsGrid({
  label, axes, columns, total, rows, sort, onSort, busy = false, after, stacked = false, headColor = 'var(--ink-500)',
  lastAxis = { min: AXIS_WIDTH, fr: 1.3 }, totalSubSize = 'var(--t-note)',
}: {
  label: string;
  axes: readonly GridAxis[];
  columns: readonly GridColumn[];
  total: { sub: string; cells: GridCell[] };
  rows: readonly GridRow[];
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  busy?: boolean;
  after?: ReactNode;
  stacked?: boolean;
  headColor?: string;
  lastAxis?: { min: number; fr: number } | null;
  totalSubSize?: string;
}) {
  const template = axes.map((_, i): string => (i === axes.length - 1 && lastAxis ? `minmax(${lastAxis.min}px,${lastAxis.fr}fr)` : `${AXIS_WIDTH}px`))
    .concat(columns.map((c) => c.track ?? `${columnWidth(c.label, c.width, c.fixed ? 0 : 36)}px`)).join(' ');
  const arrow = (k: string) => (sort.man && sort.k === k ? (sort.d === 1 ? ' ↑' : ' ↓') : '');
  const headFg = (k: string, own?: string) => (sort.man && sort.k === k ? 'var(--teal-800)' : own ?? headColor);
  const axisFg = (k: string) => (sort.man && sort.k === k ? 'var(--teal-800)' : 'var(--ink-500)');
  const pick = (k: string) => onSort(sort.man && sort.k === k ? { k, d: (-sort.d) as 1 | -1, man: true } : { k, d: k.startsWith('ax:') ? 1 : -1, man: true });
  const ariaSort = (k: string): 'ascending' | 'descending' | 'none' => (sort.man && sort.k === k ? (sort.d === 1 ? 'ascending' : 'descending') : 'none');

  return (
    <>
      <div aria-busy={busy} style={{ overflowX: 'auto', maxHeight: '74vh', overflowY: 'auto', opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
        <div role="table" aria-label={label} aria-rowcount={rows.length + 2} style={{ minWidth: 'max-content' }}>
          <div style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: template, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
              {axes.map((a, i) => (
                <div key={i} role="columnheader" aria-sort={ariaSort(`ax:${i}`)} style={{
                  display: 'flex', position: 'sticky', left: i * AXIS_WIDTH, zIndex: 2, background: 'var(--ink-50)',
                  borderRight: `1px solid ${i === axes.length - 1 ? 'var(--ink-200)' : 'var(--ink-100)'}`,
                }}>
                  <button type="button" onClick={() => pick(`ax:${i}`)} className="oz-stats-sort" title={t('Trier sur cet axe')}
                    style={{
                      flex: 1, padding: '0 12px 9px', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', background: 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}>
                    <span style={{ ...HEAD_TEXT, color: axisFg(`ax:${i}`) }}>{a.label}{arrow(`ax:${i}`)}</span>
                  </button>
                </div>
              ))}
              {columns.map((c) => (
                <div key={c.k} role="columnheader" aria-sort={ariaSort(c.k)} style={{ display: 'flex', minWidth: 0 }}>
                  <button type="button" onClick={() => pick(c.k)} className="oz-stats-sort" title={c.title ?? t('Trier sur cet indicateur')}
                    style={{
                      flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: c.sub === undefined ? 5 : 4, padding: '0 10px 8px',
                      border: 'none', background: c.headBg ?? 'transparent', cursor: 'pointer', textAlign: 'right', minWidth: 0,
                    }}>
                    <span style={{ height: 3, borderRadius: 2, background: c.color, marginTop: 8 }} />
                    {c.sub !== undefined && (
                      <span style={{
                        display: 'block', fontFamily: 'var(--font-mono)', fontSize: '8.5px', letterSpacing: '.09em', textTransform: 'uppercase',
                        color: 'var(--ink-400)', textAlign: 'right', lineHeight: 1,
                      }}>{c.sub}</span>
                    )}
                    <span style={{ ...HEAD_TEXT, color: headFg(c.k, c.headFg), lineHeight: 1.25, textAlign: 'right' }}>{c.label}{arrow(c.k)}</span>
                  </button>
                </div>
              ))}
            </div>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: template, background: 'var(--teal-50)', borderBottom: '1.5px solid var(--teal-400)' }}>
              {axes.map((_, i) => (
                <div key={i} role="rowheader" style={{
                  padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', left: i * AXIS_WIDTH, zIndex: 2,
                  background: 'var(--teal-50)', borderRight: `1px solid ${i === axes.length - 1 ? 'var(--teal-400)' : 'rgba(43,201,163,.35)'}`, minWidth: 0,
                }}>
                  {i === 0 && (
                    <>
                      <span style={{ fontSize: 'var(--t-body)', fontWeight: 800, color: 'var(--teal-800)', whiteSpace: 'nowrap' }}>{t('TOTAL')}</span>
                      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--teal-800)', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{total.sub}</span>
                    </>
                  )}
                </div>
              ))}
              {total.cells.map((x, n) => (x.lines ? (
                <LinesCell key={columns[n]?.k ?? n} cell={x} padding="9px 10px" weight={800} />
              ) : stacked ? (
                <StackedCell key={columns[n]?.k ?? n} cell={x} padding="9px 10px" size="var(--t-lead)" weight={800} subSize={totalSubSize} subWeight={700} />
              ) : (
                <div key={columns[n]?.k ?? n} role="cell" style={{
                  padding: '11px 10px', textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 800,
                  color: x.fg === 'var(--ink-800)' ? 'var(--teal-800)' : x.fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}>{x.text}</div>
              )))}
            </div>
          </div>
          {rows.map((r, n) => {
            const bg = n % 2 ? 'var(--ink-50)' : 'var(--paper)';
            return (
              <div key={r.key} role="row" className="oz-stats-rrow"
                style={{ display: 'grid', gridTemplateColumns: template, borderBottom: '1px solid var(--ink-100)', background: bg }}>
                {r.parts.map((p, i) => {
                  const code = !!axes[i]?.code;
                  return (
                    <div key={i} role="rowheader" style={{
                      padding: '9px 12px', minWidth: 0, position: 'sticky', left: i * AXIS_WIDTH, zIndex: 1, background: bg,
                      borderRight: `1px solid ${i === axes.length - 1 ? 'var(--ink-200)' : 'var(--ink-100)'}`,
                    }}>
                      <span style={{
                        display: 'block', fontSize: 'var(--t-body)', fontWeight: i === 0 ? 600 : 500, color: i === 0 ? 'var(--ink-900)' : 'var(--ink-600)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: code ? 'var(--font-mono)' : 'inherit',
                      }}>{shownValue(p.value)}</span>
                      {code && p.label !== p.value && (
                        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shownValue(p.label)}</span>
                      )}
                      {i === 0 && (
                        <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.sub}
                        </span>
                      )}
                    </div>
                  );
                })}
                {r.cells.map((x, i) => (x.lines ? (
                  <LinesCell key={columns[i]?.k ?? i} cell={x} padding="7px 10px" />
                ) : stacked ? (
                  <StackedCell key={columns[i]?.k ?? i} cell={x} padding="7px 10px" size="var(--t-body)" weight={x.fw} subSize="var(--t-meta)" subWeight={600} />
                ) : (
                  <div key={columns[i]?.k ?? i} role="cell" style={{
                    padding: '9px 10px', textAlign: 'right', fontSize: 'var(--t-body)', fontWeight: x.fw, color: x.fg,
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                  }}>{x.text}</div>
                )))}
              </div>
            );
          })}
        </div>
      </div>
      {after}
    </>
  );
}
